import { get } from "lodash";
import { getHostApiBaseUrl } from "../config/env";
import { apiClient, getTenantId } from "../services/apiClient";
import type { TaskTableField } from "../types/manualAssessmentWorkflow";

export type TaskTableAttachmentFile = {
  id?: string;
  name: string;
  size?: string | number;
  path?: string;
  downloadable?: boolean;
};

export type TaskTableAttachmentDownloadParam = {
  store?: string;
  fieldName?: string;
  valuePath?: string;
  value?: unknown;
};

export type TaskTableAttachmentDownloadConfig = {
  url?: string;
  basicUrl?: string;
  params?: TaskTableAttachmentDownloadParam[];
  requestMethod?: string;
  openTarget?: string;
  headers?: Record<string, string>;
};

const DEFAULT_DOWNLOAD_URL = "/pm/file/download";

const DEFAULT_DOWNLOAD_PARAMS: TaskTableAttachmentDownloadParam[] = [
  { store: "file", fieldName: "fileName", valuePath: "name" },
  {
    store: "extraState",
    fieldName: "taskInstanceUniqueId",
    valuePath: "taskInstanceUniqueId",
  },
];

export function isTaskTableAttachmentField(
  field: TaskTableField | undefined,
): boolean {
  if (!field) return false;
  if (String(field.type ?? "").trim().toLowerCase() === "attachment") {
    return true;
  }
  const fromField =
    typeof field.field === "string" ? field.field.trim() : "";
  const fromName = typeof field.name === "string" ? field.name.trim() : "";
  return fromField === "taskAttachment" || fromName === "taskAttachment";
}

/** Prefer Figma "Attachment" when backend still sends "Attachment Upload". */
export function getTaskTableAttachmentColumnLabel(
  field: TaskTableField | undefined,
): string {
  const raw = typeof field?.label === "string" ? field.label.trim() : "";
  if (!raw || raw.toLowerCase() === "attachment upload") {
    return "Attachment";
  }
  return raw;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function parseAttachmentFile(value: unknown): TaskTableAttachmentFile | null {
  if (!isRecord(value)) return null;
  const name = typeof value.name === "string" ? value.name.trim() : "";
  if (!name) return null;
  return {
    name,
    ...(typeof value.id === "string" || typeof value.id === "number"
      ? { id: String(value.id) }
      : {}),
    ...(value.size != null ? { size: value.size as string | number } : {}),
    ...(typeof value.path === "string" ? { path: value.path } : {}),
    ...(typeof value.downloadable === "boolean"
      ? { downloadable: value.downloadable }
      : {}),
  };
}

/** Normalize table-cell fieldValue (array, JSON string, or single object). */
export function parseTaskTableAttachments(
  value: unknown,
): TaskTableAttachmentFile[] {
  if (value == null || value === "") return [];
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      return parseTaskTableAttachments(JSON.parse(trimmed));
    } catch {
      return [{ name: trimmed }];
    }
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => parseAttachmentFile(item))
      .filter((item): item is TaskTableAttachmentFile => item != null);
  }
  const single = parseAttachmentFile(value);
  return single ? [single] : [];
}

export function canDownloadTaskTableAttachment(
  file: TaskTableAttachmentFile,
  checkDownloadable: boolean,
): boolean {
  if (!file.name) return false;
  if (checkDownloadable && file.downloadable !== true) return false;
  return true;
}

export function formatTaskTableAttachmentSize(
  size: string | number | undefined,
): string {
  if (size == null || size === "") return "";
  const bytes = typeof size === "number" ? size : Number(size);
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function joinUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}

export function normalizeTaskTableAttachmentDownloadConfig(
  config: unknown,
): TaskTableAttachmentDownloadConfig {
  const raw = isRecord(config) ? config : {};
  const url =
    typeof raw.url === "string" && raw.url.trim()
      ? raw.url.trim()
      : DEFAULT_DOWNLOAD_URL;
  const params = Array.isArray(raw.params)
    ? (raw.params as TaskTableAttachmentDownloadParam[])
    : DEFAULT_DOWNLOAD_PARAMS;
  const basicUrl =
    typeof raw.basicUrl === "string" && raw.basicUrl.length > 0
      ? raw.basicUrl
      : url.startsWith("/pm/")
        ? getHostApiBaseUrl()
        : undefined;

  return {
    ...raw,
    url,
    params,
    ...(basicUrl ? { basicUrl } : {}),
    requestMethod:
      typeof raw.requestMethod === "string" ? raw.requestMethod : "GET",
    openTarget: typeof raw.openTarget === "string" ? raw.openTarget : "_self",
  };
}

export function resolveTaskTableAttachmentDownloadUrl(
  config: TaskTableAttachmentDownloadConfig,
): string {
  const target = config.url ?? "";
  if (!target) return "";
  if (/^https?:\/\//i.test(target)) return target;
  const base = config.basicUrl || getHostApiBaseUrl();
  return joinUrl(base, target);
}

export function resolveTaskTableAttachmentDownloadParams(
  params: TaskTableAttachmentDownloadParam[] | undefined,
  store: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  params?.forEach((param) => {
    const fieldName =
      param.fieldName ?? (param.valuePath === "all" ? "" : param.valuePath);
    if (!fieldName) return;
    const source = param.store ? store[param.store] : undefined;
    const rawValue =
      param.value !== undefined
        ? param.value
        : param.valuePath === "all"
          ? source
          : get(source, param.valuePath ?? "", null);
    if (rawValue === undefined || rawValue === null) return;
    result[fieldName] = rawValue;
  });
  return result;
}

function hasValue(value: unknown): boolean {
  return value !== undefined && value !== null;
}

export function extractAttachmentDownloadHref(responseData: unknown): string {
  const defaultPayload = isRecord(responseData)
    ? responseData.data
    : undefined;
  const payload =
    defaultPayload === undefined ? responseData : defaultPayload;
  const mappedUrl =
    (isRecord(payload) ? (payload.url ?? payload.value) : undefined) ??
    (typeof payload === "string" ? payload : undefined);
  return typeof mappedUrl === "string" && mappedUrl.trim() ? mappedUrl : "";
}

function triggerBrowserDownload(href: string, fileName: string, openTarget: string) {
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  if (openTarget) {
    link.target = openTarget;
    link.rel = "noopener noreferrer";
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function downloadTaskTableAttachment(options: {
  file: TaskTableAttachmentFile;
  downloadConfig: TaskTableAttachmentDownloadConfig;
  extraState: Record<string, unknown>;
  checkDownloadable?: boolean;
}): Promise<void> {
  const { file, downloadConfig, extraState, checkDownloadable = false } =
    options;
  if (!canDownloadTaskTableAttachment(file, checkDownloadable)) {
    throw new Error("Download failed");
  }

  const href = resolveTaskTableAttachmentDownloadUrl(downloadConfig);
  if (!href) {
    throw new Error("Download failed");
  }

  const params = resolveTaskTableAttachmentDownloadParams(downloadConfig.params, {
    file,
    extraState,
  });
  const tenantId = getTenantId();
  const response = await apiClient.get(href, params, {
    skipDomain: true,
    headers: {
      ...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
      ...(downloadConfig.headers ?? {}),
    },
  });

  const downloadUrl = extractAttachmentDownloadHref(response);
  if (!hasValue(downloadUrl) || typeof downloadUrl !== "string" || !downloadUrl) {
    throw new Error("Download failed");
  }

  triggerBrowserDownload(
    downloadUrl,
    file.name,
    downloadConfig.openTarget ?? "_self",
  );
}
